import {
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  animate,
  AnimationEvent,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { DomHandler } from 'primeng/dom';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { TablerIconComponent } from 'angular-tabler-icons';
import { LayoutService } from 'src/app/shared/services/layout.service';

@Component({
  selector: '[app-menuitem]',
  imports: [CommonModule, RouterModule, RippleModule, TooltipModule, TablerIconComponent],
  templateUrl: './app-menuitem.component.html',
  styleUrl: './app-menuitem.component.css',
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        }),
      ),
      state(
        'expanded',
        style({
          height: '*',
        }),
      ),
      state(
        'hidden',
        style({
          display: 'none',
        }),
      ),
      state(
        'visible',
        style({
          display: 'block',
        }),
      ),
      transition(
        'collapsed <=> expanded',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'),
      ),
    ]),
  ],
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  @Input() item: any;

  @Input() index!: number;

  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

  @Input() parentKey!: string;

  @ViewChild('submenu') submenu!: ElementRef;

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active;
  }

  @HostBinding('class.route-active-menuitem')
  get routeActiveClass() {
    // Static mode: root item with children → only highlight when collapsed.
    // When expanded the matching child leaf carries the highlight instead.
    const isStaticMode = !this.isCompact() && !this.isSlim() && !this.isHorizontal();
    if (this.root && this.item?.items?.length && isStaticMode) {
      return !this.active && this._routeActive();
    }
    return this._routeActive();
  }

  active = false;

  menuSourceSubscription: Subscription;

  menuResetSubscription: Subscription;

  key: string = '';

  public layoutService = inject(LayoutService);
  public router = inject(Router);

  /** Signal that updates on every NavigationEnd — drives _routeActive */
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /**
   * True when this item (or any of its menu-item descendants) matches the
   * current route exactly.  Recomputes automatically on every navigation.
   */
  private _routeActive = computed(() => {
    this.currentUrl(); // reactive dependency — re-runs on navigation
    if (!this.item) return false;
    if (this.item.routerLink) {
      const path = Array.isArray(this.item.routerLink)
        ? this.item.routerLink[0]
        : this.item.routerLink;
      return this.router.isActive(path, {
        paths: 'exact',
        queryParams: 'ignored',
        matrixParams: 'ignored',
        fragment: 'ignored',
      });
    }
    if (this.item.items?.length) {
      return this._isAnyChildActive(this.item.items);
    }
    return false;
  });

  private _isAnyChildActive(items: any[]): boolean {
    if (!items?.length) return false;
    return items.some(child => {
      if (child.routerLink) {
        const path = Array.isArray(child.routerLink)
          ? child.routerLink[0]
          : child.routerLink;
        return this.router.isActive(path, {
          paths: 'exact',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored',
        });
      }
      return this._isAnyChildActive(child.items);
    });
  }

  get submenuAnimation() {
    if (
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() ||
        this.layoutService.isSlim() ||
        this.layoutService.isCompact())
    ) {
      return this.active ? 'visible' : 'hidden';
    }

    return this.active ? 'expanded' : 'collapsed';
  }

  isSlim = computed(() => this.layoutService.isSlim());

  isCompact = computed(() => this.layoutService.isCompact());

  isHorizontal = computed(() => this.layoutService.isHorizontal());

  get isDesktop() {
    return this.layoutService.isDesktop();
  }

  get isMobile() {
    return this.layoutService.isMobile();
  }

  constructor() {
    this.menuSourceSubscription = this.layoutService.menuSource$.subscribe(
      (value) => {
        Promise.resolve(null).then(() => {
          if (value.routeEvent) {
            const matches =
              value.key === this.key || value.key.startsWith(this.key + '-');
            
            if (!(this.isCompact() && this.root)) {
              this.active = matches;
            }
          } else {
            if (
              value.key !== this.key &&
              !value.key.startsWith(this.key + '-')
            ) {
              this.active = false;
            }
          }
        });
      },
    );

    this.menuResetSubscription = this.layoutService.resetSource$.subscribe(
      () => {
        this.active = false;
      },
    );

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((params) => {
        if (this.isCompact() || this.isSlim() || this.isHorizontal()) {
          this.active = false;
        } else {
          if (this.item.routerLink) {
            this.updateActiveStateFromRoute();
          }
        }
      });

    effect(() => {
      if (
        this.layoutService.isOverlay() &&
        this.layoutService.isSidebarActive()
      ) {
        if (this.item.routerLink) {
          this.updateActiveStateFromRoute();
        }
      }
    });
  }

  /** true, wenn der Icon-String noch eine PrimeIcons-Klasse ist
      (z. B. Backend-Icons von Dashboards/Modulen) — Fallback-Rendering. */
  isPiIcon(icon?: string): boolean {
    return !!icon && (icon.startsWith('pi ') || icon.startsWith('pi-'));
  }

  ngOnInit() {
    this.key = this.parentKey
      ? this.parentKey + '-' + this.index
      : String(this.index);

    if (
      !(this.isCompact() || this.isSlim() || this.isHorizontal()) &&
      this.item.routerLink
    ) {
      this.updateActiveStateFromRoute();
    }
  }

  get overlaySubmenuMode() {
    return (
      this.isDesktop &&
      (this.isHorizontal() || this.isSlim() || this.isCompact())
    );
  }

  get submenuClass() {
    // Overlay-Modi brauchen das "floating" submenu (absolute + z-index)
    return this.overlaySubmenuMode
      ? 'layout-root-submenulist !z-50'
      : 'layout-submenu';
  }

  ngAfterViewChecked() {
    if (
      this.root &&
      this.active &&
      this.isDesktop &&
      (this.isHorizontal() || this.isSlim() || this.isCompact())
    ) {
      this.calculatePosition(
        this.submenu?.nativeElement,
        this.submenu?.nativeElement.parentElement,
      );
    }
  }

  updateActiveStateFromRoute() {
    if (this.item && this.item.routerLink) {
      const path = Array.isArray(this.item.routerLink)
        ? this.item.routerLink[0]
        : this.item.routerLink;
      const activeRoute = this.router.isActive(path, {
        paths: 'exact',
        queryParams: 'ignored',
        matrixParams: 'ignored',
        fragment: 'ignored',
      });
      if (activeRoute) {
        this.layoutService.onMenuStateChange({
          key: this.key,
          routeEvent: true,
        });
      }
    }
  }

  onSubmenuAnimated(event: AnimationEvent) {
    if (
      event.toState === 'visible' &&
      this.isDesktop &&
      (this.isHorizontal() || this.isSlim() || this.isCompact())
    ) {
      const el = <HTMLUListElement>event.element;
      const elParent = <HTMLUListElement>el.parentElement;
      this.calculatePosition(el, elParent);
    }
  }

  calculatePosition(overlay: HTMLElement, target: HTMLElement) {
    if (overlay) {
      const { left, top } = target.getBoundingClientRect();
      const [vWidth, vHeight] = [window.innerWidth, window.innerHeight];
      const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
      const scrollbarWidth = DomHandler.calculateScrollbarWidth();
      // reset
      overlay.style.top = '';
      overlay.style.left = '';

      if (this.layoutService.isHorizontal()) {
        const width = left + oWidth + scrollbarWidth;
        overlay.style.left =
          vWidth < width ? `${left - (width - vWidth)}px` : `${left}px`;
      } else if (
        this.layoutService.isSlim() ||
        this.layoutService.isCompact()
      ) {
        const height = top + oHeight;
        overlay.style.top =
          vHeight < height ? `${top - (height - vHeight)}px` : `${top}px`;
      }
    }
  }

  itemClick(event: Event) {
  // avoid processing disabled items
  if (this.item.disabled) {
    event.preventDefault();
    return;
  }

  // navigate with hover
  if ((this.root && this.isSlim()) || this.isHorizontal() || this.isCompact()) {
    this.layoutService.layoutState.update((val) => ({
      ...val,
      menuHoverActive: !val.menuHoverActive,
    }));
  }

  // execute command
  if (this.item.command) {
    this.item.command({ originalEvent: event, item: this.item });
  }

  // toggle active state
  if (this.item.items?.length) {
    this.active = !this.active;

    if (
      this.root &&
      this.active &&
      (this.isSlim() || this.isHorizontal() || this.isCompact())
    ) {
      this.layoutService.onOverlaySubmenuOpen();
    }
  } else {
    if (this.layoutService.layoutState().dynamicSidebar) {
      this.layoutService.layoutConfig.update((state) => ({
        ...state,
        menuMode: 'compact', //
      }));
    }

    if (this.layoutService.isMobile()) {
      this.layoutService.layoutState.update((val) => ({
        ...val,
        staticMenuMobileActive: false,
      }));
    }

    if (this.isSlim() || this.isHorizontal() || this.isCompact()) {
      this.layoutService.reset();
      this.layoutService.layoutState.update((val) => ({
        ...val,
        menuHoverActive: false,
      }));
    }
  }

  this.layoutService.onMenuStateChange({ key: this.key });
}


  onMouseEnter() {
    // activate item on hover
    if (
      this.root &&
      (this.isSlim() || this.isHorizontal() || this.isCompact()) &&
      this.layoutService.isDesktop()
    ) {
      if (this.layoutService.layoutState().menuHoverActive) {
        this.active = true;
        this.layoutService.onMenuStateChange({ key: this.key });
      }
    }
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
